<template>
    <component
        :is="element.is"
        v-bind="{ ...$attrs, ...element.attributes }"
        class="sidebar-nav-item"
        :class="{ active }"
        :aria-current="active ? 'page' : undefined"
    >
        <component :is="icon" v-if="icon" :size="20" class="sidebar-nav-item__icon" weight="regular" />
        <span class="sidebar-nav-item__label">{{ label }}</span>
        <span v-if="$slots.trailing || badge != null" class="sidebar-nav-item__trailing">
            <slot name="trailing">
                <span class="sidebar-nav-item__count" :aria-hidden="badgeLabel ? 'true' : undefined">{{ badge }}</span>
                <span v-if="badgeLabel" class="sr-only">{{ badgeLabel }}</span>
            </slot>
        </span>
    </component>
</template>

<script setup lang="ts">
import { computed, type Component } from 'vue';
import { NuxtLink } from '#components';

defineOptions({ inheritAttrs: false });

const props = defineProps<{
    label: string;
    href?: string;
    active?: boolean;
    icon?: Component;
    external?: boolean;
    /** A count in the item's trailing slot, set as DESIGN.md's count chip. */
    badge?: number | string | null;
    /**
     * What the count counts, in words, read in place of the bare figure: a number after a
     * link's name says nothing to a reader who cannot see the chip it sits in.
     */
    badgeLabel?: string;
}>();

/** The element the item renders as, with the attributes that carry its destination. */
const element = computed(() => {
    if (props.href && !props.external) return { is: NuxtLink, attributes: { to: props.href } };
    if (props.href) return { is: 'a', attributes: { href: props.href, target: '_blank', rel: 'noopener noreferrer' } };

    return { is: 'button', attributes: { type: 'button' } };
});
</script>

<style lang="scss" scoped>
.sidebar-nav-item {
    @apply flex items-center gap-2 w-full px-3 rounded text-left bg-transparent border-0 cursor-pointer no-underline;
    @apply text-ink-invert text-sm font-semibold leading-none;
    // DESIGN.md, The rail: a nav item is a control's height, the height the nav's scroll padding
    // counts for its foot, so the two read one token.
    height: var(--control-h);
    transition: background-color 0.15s ease-in-out;

    &:hover {
        @apply bg-white/5;
    }

    &.active {
        @apply bg-rail-raised;
    }

    &__icon {
        @apply text-zinc-400 shrink-0;
        .sidebar-nav-item.active &,
        .sidebar-nav-item:hover & {
            @apply text-ink-invert;
        }
    }

    &__label {
        @apply flex-1 truncate;
    }

    &__trailing {
        @apply shrink-0 flex items-center;
    }

    // DESIGN.md, The rail: the count chip. Its fill is the rail's 10% white, the value
    // --rail-hairline names, and its minimum width lines a one-digit chip up with a four-digit one.
    &__count {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-width: var(--space-6);
        padding: 0 var(--space-1-5);
        border-radius: var(--radius-full);
        background: var(--rail-hairline);
        color: var(--rail-ink);
        font-size: var(--text-2xs);
        line-height: var(--leading-2xs);
        font-weight: var(--weight-semibold);
        font-variant-numeric: tabular-nums;
    }
}
</style>
